import { Country } from '@afterpaytouch/portal-api/types'
import { BiometricsCheckTrigger, CheckoutError, CrossRegionRedirection, InitialData, OtpLoginRouteAndTriggerResult, ConvergenceRollout } from '../../model'
import { AsyncAction } from '../../state'
import { PublicResponse } from '@afterpay/verify-service-api'
import { InitialiseNextRoute } from './routing'
import {
  performInitialSetup,
  fetchAndValidateCheckout,
  handlePersistentLogin,
  handleConsolidatedConsumerLookup,
  prepareLookupData,
  performSequentialLookup,
  handleInitialiseError,
  SetupRequest,
  CheckoutFetchRequest,
  ConsumerLookupRequest,
  LookupDataPreparationRequest,
} from './orchestrators'
import { capture } from '@afterpaytouch/integrations/monitoring'

type InitialiseResult = InitialiseNextRoute | CheckoutError | CrossRegionRedirection | PublicResponse | undefined

export interface InitialiseRequest {
  initialData: InitialData | null
  country: Country
  otpLoginRouteAndTrigger: OtpLoginRouteAndTriggerResult
  redirectToBillingAddressEnabled: boolean
  biometricsEligibleAndRegisteredCheckTrigger: BiometricsCheckTrigger
  referrer: string | null // = null,
  usernameFromCookie: string | null // = null,
  skipPrefill: boolean // = false
  rollout: ConvergenceRollout
  logEvent: (event: string, data?: Record<string, unknown>) => void
}

export const initialise =
  (request: InitialiseRequest): AsyncAction<InitialiseResult> =>
  async (dispatch) => {
    try {
      // Step 1: Initial Setup
      const setupRequest: SetupRequest = {
        initialData: request.initialData,
        country: request.country,
        referrer: request.referrer,
        isIframe: request.initialData?.isIframe ?? false,
      }

      const initialSetupResult = await performInitialSetup(setupRequest, dispatch)
      if (initialSetupResult.error !== null) {
        capture.warning('Checkout session without token')
        return initialSetupResult.error
      }
      const initialData = initialSetupResult.initialData as InitialData
      const browserCapabilities = {
        canSupportBiometric: initialSetupResult.browserCapabilities?.canSupportBiometric ?? false,
        isAuthenticatorAvailable: initialSetupResult.browserCapabilities?.isAuthenticatorAvailable ?? false,
      }

      // Step 2: Fetch & Validate Checkout
      const checkoutRequest: CheckoutFetchRequest = { country: request.country, skipPrefill: request.skipPrefill }
      const getCheckoutResponse = await fetchAndValidateCheckout(checkoutRequest, dispatch)

      if ('route' in getCheckoutResponse || 'crossRegionCheckoutUrl' in getCheckoutResponse) {
        // three possible route:
        // 1. terminal error
        // 2. skip prefill enter email
        // 3. cross region checkout url
        return getCheckoutResponse
      }

      const { checkout } = getCheckoutResponse

      // Step 3: Persistent Login
      const persistentLoginResult = await handlePersistentLogin(checkout, dispatch)

      if (persistentLoginResult !== null) {
        return persistentLoginResult
      }

      // Step 4: Consolidated Lookup Flow in getCheckoutData
      if (checkout.consumerLookup != null) {
        const consumerLookupRequest: ConsumerLookupRequest = {
          checkout,
          consumerLookup: checkout.consumerLookup,
          logEvent: request.logEvent,
        }
        return handleConsolidatedConsumerLookup(consumerLookupRequest, dispatch)
      }

      // Step 5: Traditional Sequential Lookup
      // 5.1: Email lookup
      // 5.2: Mobile lookup
      const lookupDataRequest: LookupDataPreparationRequest = {
        checkout,
        country: request.country,
        validInitialData: initialData,
        browserCapabilities,
        redirectToBillingAddressEnabled: request.redirectToBillingAddressEnabled,
        usernameFromCookie: request.usernameFromCookie,
        biometricsEligibleAndRegisteredCheckTrigger: request.biometricsEligibleAndRegisteredCheckTrigger,
        otpLoginRouteAndTrigger: request.otpLoginRouteAndTrigger,
        rollout: request.rollout,
      }

      const lookupData = prepareLookupData(lookupDataRequest)

      return await performSequentialLookup(
        {
          ...lookupData,
          checkout,
        },
        dispatch
      )
    } catch (error) {
      return handleInitialiseError(error)
    }
  }
